// src/pages/BranchList.jsx
import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import { branchApi } from "../Apis/Api";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import LoadingComponent from "../components/ui/LoadingComponent";
import useDebounce from "../hook/useDebounce";
import Breadcrumb from "../components/Breadcrumb";
import { formatDateTime } from "../utils/formatDateTime";
import { TextWrap } from "../utils/textWrap";
import PaginationControls from "../components/ui/PaginationControls";
import { usePermissions } from "../context/PermissionContext";
import CardContainer from "../components/ui/CardContainer";
import PerPageDropdown from "../components/ui/PerPageDropdown";
import ErrorAlert from "../components/ui/ErrorAlert";
import SortIcons from "../components/ui/SortIcons";
import StatusBadge from "../components/ui/StatusBadge";
import { AddButton } from "../components/ui/Button";
import { handleApiResponse } from "../utils/response";
import InfoModal from "../components/ui/InfoeModal";

const BranchList = () => {
  const navigate = useNavigate();
  const [clientBranches, setClientBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const searchInputRef = useRef(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const debouncedSearch = useDebounce(filters.search, 500);

  useEffect(() => {
    fetchClientBranches();
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearch,
    filters.status,
    sortConfig,
  ]);

  const fetchClientBranches = async () => {
    setLoading(true);
    setError(null);
    try {
      const fieldMapping = {
        branch_code: "branch_code",
        branch_name: "branch_name",
        phone_number: "phone_number",
        address: "address",
        city_id: "city_id",
        city_name: "city_name",
        country_id: "country_id",
        country_name: "country_name",
        created_by: "created_by",
        is_active: "is_active",
        created_datetime: "created_datetime",
      };

      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearch,
        ...(filters.status && {
          is_active: filters.status === "Active" ? true : false,
        }),
        ...(sortConfig.field && {
          sort_by: fieldMapping[sortConfig.field] || sortConfig.field,
          sort_order: sortConfig.direction,
        }),
      };

      const response = await branchApi.getClientBranches(params);

      if (!Array.isArray(response.data)) {
        throw new Error("Invalid API response: data is not an array");
      }
      if (!response.pagination || typeof response.pagination !== "object") {
        throw new Error("Invalid API response: pagination data missing");
      }

      const data = response.data.map((branch, index) => ({
        id: branch.branch_id || index + 1,
        branch_code: branch.branch_code || "-",
        branch_name: branch.branch_name || "-",
        phone_number: branch.phone_number || "-",
        address: branch.address || "-",
        city_id: branch.city_id || "-",
        city_name: branch.city_name || "-",
        country_id: branch.country_id || "-",
        country_name: branch.country_name || "-",
        tax_id: branch?.taxes?.map((tax) => tax.tax_id) || [],
        created_by: branch.created_by || "-",
        is_active:
          branch.is_active !== undefined && branch.is_active !== null
            ? branch.is_active
              ? "Active"
              : "Inactive"
            : "-",
        created_datetime: branch.created_datetime || "-",
        display_created_datetime: branch.created_datetime
          ? formatDateTime(branch.created_datetime)
          : "-",
      }));

      if (sortConfig.field) {
        data.sort((a, b) => {
          const valueA = a[sortConfig.field] || "";
          const valueB = b[sortConfig.field] || "";
          if (sortConfig.field === "created_datetime") {
            const dateA = new Date(a.created_datetime || 0);
            const dateB = new Date(b.created_datetime || 0);
            return sortConfig.direction === "asc"
              ? dateA - dateB
              : dateB - dateA;
          }
          return sortConfig.direction === "asc"
            ? valueA.toString().localeCompare(valueB.toString(), undefined, {
                sensitivity: "base",
              })
            : valueB.toString().localeCompare(valueA.toString(), undefined, {
                sensitivity: "base",
              });
        });
      }

      setClientBranches(data);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.pagination?.current_page || 1,
        perPage: response.pagination?.per_page || 10,
        total: response.pagination?.total || data.length,
      }));
      setError(null);
      handleApiResponse(response, "fetch client branches", "GET");
    } catch (err) {
      const errorMessage =
        err?.message ||
        "Failed to fetch client branches. Please check the API endpoint.";
      setError(errorMessage);
      setClientBranches([]);
      handleApiResponse(err, "Failed to fetch client branches", "GET");
    } finally {
      setLoading(false);
    }
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusFilterChange = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: status === "Select Status" ? "" : status,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleEdit = (branch) => {
    navigate(`/branch-edit`, { state: { branchData: branch } });
  };

  const handleDeleteClick = (branchId, branchName) => {
    setBranchToDelete({ id: branchId, name: branchName });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (branchToDelete) {
      setLoading(true);
      try {
        const response = await branchApi.deleteBranch(branchToDelete.id);
        handleApiResponse(
          response,
          `Branch "${branchToDelete.name}" deleted successfully`,
          "DELETE"
        );
        setClientBranches((prev) =>
          prev.filter((branch) => branch.id !== branchToDelete.id)
        );
        setPagination((prev) => ({
          ...prev,
          total: prev.total - 1,
          currentPage: Math.min(
            prev.currentPage,
            Math.ceil((prev.total - 1) / prev.perPage) || 1
          ),
        }));
        setShowDeleteModal(false);
        setBranchToDelete(null);
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message || "Failed to delete branch";
        handleApiResponse(err, `Failed to delete branch`, "DELETE");
      } finally {
        setLoading(false);
        setShowDeleteModal(false);
        setBranchToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setBranchToDelete(null);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchClientBranches} />;
  }

  const handleAddLocationWithBranch = (branch) => {
    navigate("/branch-location-create", {
      state: {
        branchData: {
          id: branch.id,
          name: branch.branch_name,
          address: branch.address || "",
          country_name: branch.country_name || "UAE",
        },
        navigationLink: { link: "branch-locations", isLink: true },
      },
    });
  };

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search"
                placeholder="Search by Branch Name, Branch Code, City or Country"
                value={filters.search}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search branches by name or address"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          <div className="d-flex align-items-center gap-2"> {/* 👈 UPDATED: Added Info Button */}
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Branch List Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
            <AddButton
              label="Add New Branch"
              permissionKey="add_client_branch"
              onClick={() => navigate("/branch-create")}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Branch List" />
      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {clientBranches.length === 0 && !loading ? (
            <TableDataNoAvailable
              message={
                filters.search || filters.status
                  ? "No branches match your search or filter."
                  : "No client branches available."
              }
            />
          ) : (
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("branch_code")}
                    role="button"
                    aria-label={`Sort by branch code, currently sorted ${
                      sortConfig.field === "branch_code"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Branch Code
                      <SortIcons field="branch_code" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("branch_name")}
                    role="button"
                    aria-label={`Sort by branch name, currently sorted ${
                      sortConfig.field === "branch_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Branch Name
                      <SortIcons field="branch_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("address")}
                    role="button"
                    aria-label={`Sort by address, currently sorted ${
                      sortConfig.field === "address"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Address
                      <SortIcons field="address" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("phone_number")}
                    role="button"
                    aria-label={`Sort by phone number, currently sorted ${
                      sortConfig.field === "phone_number"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Phone Number
                      <SortIcons field="phone_number" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("city_name")}
                    role="button"
                    aria-label={`Sort by city, currently sorted ${
                      sortConfig.field === "city_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      City
                      <SortIcons field="city_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("country_name")}
                    role="button"
                    aria-label={`Sort by country, currently sorted ${
                      sortConfig.field === "country_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Country
                      <SortIcons field="country_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_by")}
                    role="button"
                    aria-label={`Sort by created by, currently sorted ${
                      sortConfig.field === "created_by"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created By
                      <SortIcons field="created_by" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="text-center cursor-pointer"
                    onClick={() => handleSort("is_active")}
                    role="button"
                    aria-label={`Sort by status, currently sorted ${
                      sortConfig.field === "is_active"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center justify-content-center">
                      Status
                      <SortIcons field="is_active" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_datetime")}
                    role="button"
                    aria-label={`Sort by created date, currently sorted ${
                      sortConfig.field === "created_datetime"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created Date
                      <SortIcons
                        field="created_datetime"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {clientBranches.map((branch, index) => (
                  <tr key={branch.id}>
                    <td>
                      {(pagination.currentPage - 1) * pagination.perPage +
                        index +
                        1}
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {branch.branch_code}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {branch.branch_name}
                      </span>
                    </td>
                    <TextWrap location={branch.address} />
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {branch.phone_number}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {branch.city_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {branch.country_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {branch.created_by}
                      </span>
                    </td>
                    <td className="">
                      <StatusBadge status={branch.is_active} />
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {branch.display_created_datetime}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("add_branch_location") && (
                          <button
                            type="button"
                            onClick={() => handleAddLocationWithBranch(branch)}
                            className="bg-primary-50 text-primary-600 bg-hover-primary-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title={`Add location with branch ${branch.branch_name}`}
                            aria-label={`Add location with branch ${branch.branch_name}`}
                          >
                            <Icon
                              icon="mdi:map-marker-radius-outline"
                              className="menu-icon"
                            />
                          </button>
                        )}
                        {hasPermission("update_client_branch") && (
                          <button
                            type="button"
                            onClick={() => handleEdit(branch)}
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title={`Edit ${branch.branch_name}`}
                            aria-label={`Edit branch ${branch.branch_name}`}
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {hasPermission("delete_client_branch") && (
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteClick(branch.id, branch.branch_name)
                            }
                            className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            title={`Delete ${branch.branch_name}`}
                            aria-label={`Delete branch ${branch.branch_name}`}
                          >
                            <Icon
                              icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* 👈 EXISTING: Delete Modal */}
      <div
        className={`modal fade ${showDeleteModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: showDeleteModal ? "block" : "none",backgroundColor: showDeleteModal ? "rgba(0,0,0,0.5)" : "" 
 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Deletion</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleDeleteCancel}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="text-neutral-900">
                Are you sure you want to delete{" "}
                <strong>{branchToDelete?.name || "this branch"}</strong>?
              </p>
              <p className="text-neutral-900 small">
                If you delete this branch, all associated locations and slots
                will also be permanently deleted.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
                aria-label="Cancel deletion"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                aria-label={`Confirm delete branch ${
                  branchToDelete?.name || ""
                }`}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteModal && <div className="no-modal fade show"></div>}

      {/* 👈 NEW: Info Modal - EXACTLY SAME AS Previous Pages */}
     <InfoModal
  isOpen={showInfoModal}
  onClose={handleInfoClose}
  title="Branch Management - Complete Overview"
>
  <p className="text-neutral-900 mb-4">
    This is the <strong>central hub</strong> for managing all your business <strong>Branches</strong> and their associated <strong>Locations</strong>. 
    Admins can create, view, edit, and organize branches across different countries and cities with full geographical mapping support.
  </p>

  <h6 className="fw-bold text-primary-600 mb-3">What You Can Do Here</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">View complete branch details (Code, Name, Address, Phone, City, Country)</li>
    <li className="list-group-item">Add new branches with smart tax detection based on country</li>
    <li className="list-group-item">Edit or delete existing branches</li>
    <li className="list-group-item">Create multiple <strong>Locations</strong> under each branch with interactive polygon mapping</li>
    <li className="list-group-item">Auto-apply country-specific taxes when creating branches</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Add New Branch – Smart Fields</h6>
  <p className="small text-neutral-600 mb-2">Click "Add New Branch" to create a branch with intelligent tax handling:</p>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Country</strong> → Auto-detects and displays applicable taxes</li>
    <li className="list-group-item"><strong>City</strong> → Filters based on selected country</li>
    <li className="list-group-item"><strong>Branch Name</strong> → Unique name for the branch</li>
    <li className="list-group-item"><strong>Phone & Address</strong> → Full contact and location details</li>
    <li className="list-group-item">
      <strong>Tax Configuration</strong> → 
      <span className="text-success-600 fw-bold"> Smart Feature!</span><br/>
      All taxes applicable to the selected country appear as checkboxes → Just tick to apply!
    </li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Actions Available Per Branch</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">
      <strong>Add Location (Map Icon)</strong><br/>
      <span className="text-success-600 fw-bold">Most Powerful Feature</span><br/>
      → Opens map interface<br/>
      → Branch name pre-filled automatically<br/>
      → Draw polygons on map to define delivery/service zones<br/>
      → Save → Location created & linked to this branch
    </li>
    <li className="list-group-item">
      <strong>Edit (Pencil Icon)</strong><br/>
      Update branch details, address, phone, or tax settings
    </li>
    <li className="list-group-item">
      <strong>Delete (Trash Icon)</strong><br/>
      Permanently remove branch (only if no active locations or assignments)
    </li>
  </ul>

  <div className="alert alert-info small mt-4">
    <strong>Pro Tips & Notes:</strong><br/>
    • A branch without locations is just a placeholder — always create at least one location<br/>
    • Locations with polygon boundaries are used for delivery zoning, incentives, and workflow routing<br/>
    • Tax rules are auto-fetched from system database — no manual entry needed<br/>
    • Deleting a branch with active locations/workflow assignments is blocked for safety
  </div>

  <p className="text-neutral-900 mt-4 fw-bold text-primary">
    Best Practice: Create Branch → Immediately click "Add Location" → Draw zones → Start assigning workflows & incentives!
  </p>
  <p className="text-neutral-900 small">
    Business Impact: Properly structured branches + mapped locations = Accurate incentives, smooth operations, and happy field teams.
  </p>
</InfoModal>
    </>
  );
};

export default BranchList;